import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getItem } from '#/shared/utils/storage';
import {
  MANUAL_LOCATION_STORAGE_KEY,
  GEOLOCATION_APPROVED_STORAGE_KEY,
  SERVICE_STATE,
} from '#/shared/constants';
import { positionToLocation } from '#/shared/utils/location';
import type { RootState } from '#/state/store';
import type {
  LocationWithState,
  LocationWithGeocodedFeature,
} from '#/types/Location';

type GeocodedLocation = LocationWithState & LocationWithGeocodedFeature;

interface Coords {
  latitude: number;
  longitude: number;
}

type CurrentPositionError = Pick<GeolocationPositionError, 'code' | 'message'>;

interface CurrentPosition {
  coords: Coords | null;
  error: CurrentPositionError | null;
  fetching: boolean;
  outOfBounds: boolean;
}

interface LocationState {
  currentPosition: CurrentPosition;
  currentLocation: LocationWithState | null;
  selectedLocation: LocationWithState | null;
  geolocationApproved: boolean | null;
  geolocationPause: boolean;
  isManualLocation: boolean;
  watchId: number | null;
}

export const initialState: LocationState = {
  currentPosition: {
    coords: null,
    error: null,
    fetching: false,
    outOfBounds: false,
  },
  currentLocation: getItem(MANUAL_LOCATION_STORAGE_KEY, null),
  selectedLocation: null,
  geolocationApproved: getItem(GEOLOCATION_APPROVED_STORAGE_KEY, null),
  geolocationPause: false,
  isManualLocation: !!getItem(MANUAL_LOCATION_STORAGE_KEY, null),
  watchId: null,
};

const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    positionFetching(
      state,
      action: PayloadAction<{ attemptingGeoRefresh: boolean }>
    ) {
      const attemptingGeoRefresh =
        action.payload?.attemptingGeoRefresh ?? false;
      state.currentPosition = {
        error: null,
        fetching: true,
        coords: null,
        outOfBounds: attemptingGeoRefresh,
      };
    },
    positionError(state, action: PayloadAction<CurrentPositionError>) {
      return {
        ...state,
        currentPosition: {
          error: action.payload,
          fetching: false,
          coords: null,
          outOfBounds: false,
        },
        currentLocation: null,
        geolocationApproved: false,
      };
    },
    geolocationOutOfBounds(state) {
      return {
        ...state,
        geolocationApproved: false,
        currentPosition: {
          error: null,
          fetching: false,
          coords: null,
          outOfBounds: true,
        },
        watchId: null,

        // this is needed to clear the current location if the user steps out
        // of bounds while using geolocation services for their current location
        currentLocation: !state.isManualLocation ? null : state.currentLocation,
      };
    },
    positionReverseGeocoded(
      state,
      action: PayloadAction<{ coords: Coords; name: string }>
    ) {
      return {
        ...state,
        currentPosition: {
          error: null,
          fetching: false,
          coords: action.payload.coords,
          outOfBounds: false,
        },
        currentLocation: positionToLocation(
          action.payload,
          action.payload.name
        ),
        isManualLocation: false,
        geolocationApproved: true,
      };
    },
    manualLocationReverseGeocoded(
      state,
      action: PayloadAction<GeocodedLocation>
    ) {
      return {
        ...state,
        currentLocation: {
          lat: action.payload.lat,
          lon: action.payload.lon,
          name: action.payload.name,
          state: action.payload.state ?? SERVICE_STATE,
        },
        isManualLocation: true,
      };
    },
    manualLocationRemoved(state) {
      return {
        ...state,
        currentLocation: null,
        isManualLocation: false,
      };
    },
    watchStarted(state, action: PayloadAction<{ watchId: number }>) {
      return {
        ...state,
        watchId: action.payload.watchId,
        isManualLocation: false,
        geolocationApproved: true,
      };
    },
    watchCleared(state) {
      state.watchId = null;
    },
    // TODO: refactor CurrentPositionManager to eliminate the need
    //  have another flag set in the Redux store
    watchPaused(state) {
      state.geolocationPause = true;
    },
    watchResumed(state) {
      state.geolocationPause = false;
    },
    setSelectedLocationSuccess(state, action: PayloadAction<GeocodedLocation>) {
      state.selectedLocation = action.payload;
    },
  },
});

export const {
  positionFetching,
  positionError,
  geolocationOutOfBounds,
  positionReverseGeocoded,
  manualLocationReverseGeocoded,
  manualLocationRemoved,
  watchStarted,
  watchCleared,
  watchPaused,
  watchResumed,
  setSelectedLocationSuccess,
} = locationSlice.actions;
export default locationSlice.reducer;

// define selectors for objects in the slice so their not anonymous and
// can be memoized if needed:
// https://redux.js.org/usage/deriving-data-selectors#using-selectors-effectively
export const selectCurrentLocation = (s: RootState): LocationWithState | null =>
  s.shared.location.currentLocation;
export const selectCurrentPosition = (s: RootState): CurrentPosition =>
  s.shared.location.currentPosition;
export const selectSelectedLocation = (s: RootState): GeocodedLocation | null =>
  s.shared.location.selectedLocation;
